// =============================================================================
// screens/LearnArticleScreen.tsx — Learn with CRYB article detail (LearnArticle).
//
// REVAMP v2 (Medium/Substack-grade reading experience):
//   • Full-bleed hero — the article's cover edge-to-edge under the status bar,
//     or a brand gradient fallback; native header hidden, floating glass back
//     button instead.
//   • Thin purple reading-progress bar pinned to the top (reanimated, driven
//     by scroll position).
//   • Category + level pills, display title in the app's boldest token,
//     author / date / read-time / views meta row, generous-leading lede.
//   • Markdown-lite body renderer extended: ## / ### headings, **bold**,
//     *italic*, `inline code`, [links](url), > blockquotes, fenced ``` code
//     blocks, - bullets and 1. ordered lists. Unrecognised text always falls
//     back to a paragraph — content is never dropped.
//   • "Must-see articles" rail BELOW the body (admin-curated featured,
//     excluding this article; falls back to same-level recents; hides entirely
//     when there is nothing to show).
//
// Data contract: GET /learn/:slug → { data: <article> } (unchanged) plus
// GET /learn?featured=true for the rail.
// =============================================================================
import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import {
  View, Text, StyleSheet, ActivityIndicator, ScrollView,
  TouchableOpacity, useWindowDimensions, Linking,
} from 'react-native';
import CachedImage from '../components/ui/CachedImage';
import { useNavigation, useRoute } from '@react-navigation/native';
import type { RouteProp } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import LinearGradient from 'react-native-linear-gradient';
import Animated, {
  useSharedValue, useAnimatedScrollHandler, useAnimatedStyle,
} from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Ionicons } from '../components/ui/Icon';
import type { RootStackParamList } from '../navigation/types';
import { colors, spacing, radius, fonts, getLayout } from '../constants/theme';
import { api } from '../services/api';
import { communityIcon, learnCover } from '../lib/icons';
import { CARD_GRADIENTS, levelLabel } from '../components/learn/gradients';
import MustSeeRail, { MustSeeArticle } from '../components/learn/MustSeeRail';
import QuizSheet from '../components/learn/QuizSheet';
import { GlassSurface } from '../components/glass';
import ParticleBackground from '../components/ui/ParticleBackground';
import LoadingState from '../components/ui/LoadingState';
import Asset3D from '../components/ui/Asset3D';
import { tabularNums } from '../lib/format';

type LearnArticleRoute = RouteProp<RootStackParamList, 'LearnArticle'>;
type Nav = NativeStackNavigationProp<RootStackParamList>;

// ── Markdown-lite ────────────────────────────────────────────────────────────
// Line-based block parser. Fenced code blocks are captured first (they may
// contain blank lines); the rest is grouped into runs of headings, bullets,
// ordered items, quotes and paragraphs. Anything unrecognised stays a plain
// paragraph — we never drop content.
type Block =
  | { type: 'heading'; level: 2 | 3; text: string }
  | { type: 'bullets'; items: string[] }
  | { type: 'ordered'; items: string[] }
  | { type: 'quote'; text: string }
  | { type: 'code'; text: string }
  | { type: 'paragraph'; text: string };

function parseBody(body: string): Block[] {
  const lines = body.replace(/\r\n/g, '\n').split('\n');
  const blocks: Block[] = [];
  let para: string[] = [];
  let code: string[] | null = null;

  const kindOf = (l: string) =>
    /^[-*•]\s+/.test(l) ? 'b' : /^\d+[.)]\s+/.test(l) ? 'o' : /^>\s?/.test(l) ? 'q' : 'p';

  const flushPara = () => {
    if (para.length === 0) return;
    let run: { kind: string; lines: string[] } | null = null;
    const emit = () => {
      if (!run) return;
      if (run.kind === 'b') {
        blocks.push({ type: 'bullets', items: run.lines.map((l) => l.replace(/^[-*•]\s+/, '')) });
      } else if (run.kind === 'o') {
        blocks.push({ type: 'ordered', items: run.lines.map((l) => l.replace(/^\d+[.)]\s+/, '')) });
      } else if (run.kind === 'q') {
        blocks.push({ type: 'quote', text: run.lines.map((l) => l.replace(/^>\s?/, '')).join(' ') });
      } else {
        blocks.push({ type: 'paragraph', text: run.lines.join(' ') });
      }
      run = null;
    };
    for (const l of para) {
      const k = kindOf(l);
      if (!run || run.kind !== k) { emit(); run = { kind: k, lines: [] }; }
      run.lines.push(l);
    }
    emit();
    para = [];
  };

  for (const raw of lines) {
    const trimmed = raw.trim();
    if (/^```/.test(trimmed)) {
      if (code !== null) { blocks.push({ type: 'code', text: code.join('\n') }); code = null; }
      else { flushPara(); code = []; }
      continue;
    }
    if (code !== null) { code.push(raw); continue; }
    if (!trimmed) { flushPara(); continue; }
    const h = trimmed.match(/^(#{1,6})\s+(.+)$/);
    if (h) {
      flushPara();
      blocks.push({ type: 'heading', level: h[1].length <= 2 ? 2 : 3, text: h[2] });
      continue;
    }
    para.push(trimmed);
  }
  if (code !== null) blocks.push({ type: 'code', text: code.join('\n') }); // unclosed fence — keep it
  flushPara();
  return blocks;
}

// ── Inline image extraction ──────────────────────────────────────────────────
// The article model has a single `imageUrl` (the hero) — there is no gallery
// field yet (see followUps). Authors, however, can already embed images inside
// the markdown body as ![alt](url). We pull those out into a carousel below the
// hero so they render as real images (not literal "![]()" text) and the body
// stays clean prose. Returns the collected URLs plus the body with standalone
// image lines removed. Non-standalone (inline) images are left untouched.
const IMG_LINE_RE = /^!\[[^\]]*\]\((https?:\/\/[^)\s]+)\)\s*$/;

function extractBodyImages(body: string): { urls: string[]; cleaned: string } {
  const urls: string[] = [];
  const kept: string[] = [];
  for (const line of body.replace(/\r\n/g, '\n').split('\n')) {
    const m = line.trim().match(IMG_LINE_RE);
    if (m) { urls.push(m[1]); continue; }
    kept.push(line);
  }
  return { urls, cleaned: kept.join('\n') };
}

// Inline spans: **bold**, *italic*, `code`, [label](url). Returns Text children
// so the parent paragraph keeps its base style and line wrapping.
const INLINE_RE = /(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`|\[[^\]]+\]\([^)\s]+\))/g;

function renderInline(text: string, keyPrefix: string) {
  const parts = text.split(INLINE_RE).filter((p) => p !== '');
  return parts.map((part, i) => {
    const key = `${keyPrefix}-${i}`;
    if (/^\*\*[^*]+\*\*$/.test(part)) {
      return <Text key={key} style={inlineStyles.bold}>{part.slice(2, -2)}</Text>;
    }
    if (/^\*[^*]+\*$/.test(part)) {
      return <Text key={key} style={inlineStyles.italic}>{part.slice(1, -1)}</Text>;
    }
    if (/^`[^`]+`$/.test(part)) {
      return <Text key={key} style={inlineStyles.code}>{part.slice(1, -1)}</Text>;
    }
    const link = part.match(/^\[([^\]]+)\]\(([^)\s]+)\)$/);
    if (link) {
      const url = link[2];
      return (
        <Text
          key={key}
          style={inlineStyles.link}
          onPress={() => { Linking.openURL(url).catch(() => {}); }}
        >
          {link[1]}
        </Text>
      );
    }
    return <Text key={key}>{part}</Text>;
  });
}

const VALID_LEVELS = ['beginner', 'intermediate', 'advanced'];

export default function LearnArticleScreen() {
  const navigation = useNavigation<Nav>();
  const route = useRoute<LearnArticleRoute>();
  const { slug } = route.params ?? {};
  const { width } = useWindowDimensions();
  const insets = useSafeAreaInsets();
  const layout = getLayout(width);
  const containerStyle = layout !== 'phone'
    ? { maxWidth: 680, alignSelf: 'center' as const, width: '100%' as const }
    : undefined;

  const [article, setArticle] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [more, setMore] = useState<MustSeeArticle[]>([]);
  const [moreIsFeatured, setMoreIsFeatured] = useState(true);

  // Full-bleed hero → hide the native stack header; a floating glass back
  // button replaces it (registered header stays untouched in App.tsx).
  useLayoutEffect(() => {
    navigation.setOptions({ headerShown: false });
  }, [navigation]);

  const fetchArticle = useCallback(async () => {
    if (!slug) { setLoading(false); setError(true); return; }
    try {
      const res = await api<any>(`/learn/${encodeURIComponent(String(slug))}`);
      const d: any = res.data ?? {};
      const a = d.article ?? d;
      if (res.success && a && (a.title || a.content)) { setArticle(a); setError(false); }
      else setError(true);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => { fetchArticle(); }, [fetchArticle]);

  // #59: real communities for the "what's next" section (not just a teaser card).
  const [nextCommunities, setNextCommunities] = useState<any[]>([]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api<any>('/communities?limit=8');
        const d: any = res.data || {};
        const list = d.items ?? d.communities ?? (Array.isArray(d) ? d : []);
        if (!cancelled && res.success && Array.isArray(list)) setNextCommunities(list.slice(0, 8));
      } catch { /* non-fatal — the two CTA cards still show */ }
    })();
    return () => { cancelled = true; };
  }, []);

  // "Must-see" rail: admin-curated featured articles (excluding this one).
  // Falls back to same-level recents so the section is never pointless — and
  // if the API returns nothing at all, the rail hides itself entirely.
  useEffect(() => {
    if (!article) return;
    let cancelled = false;
    (async () => {
      const excludeSelf = (list: any[]) =>
        (Array.isArray(list) ? list : []).filter((a: any) => a?.slug && a.slug !== slug);
      try {
        const res = await api<any>('/learn?featured=true&limit=10');
        const featured = excludeSelf(res.data?.articles ?? []);
        if (res.success && featured.length > 0) {
          if (!cancelled) { setMore(featured); setMoreIsFeatured(true); }
          return;
        }
      } catch { /* fall through to recents */ }
      try {
        const lvl = VALID_LEVELS.includes(String(article.difficulty || '').toLowerCase())
          ? String(article.difficulty).toLowerCase()
          : null;
        const res = await api<any>(lvl ? `/learn?difficulty=${lvl}&limit=10` : '/learn?limit=10');
        const recents = excludeSelf(res.data?.articles ?? []);
        if (res.success && !cancelled) { setMore(recents); setMoreIsFeatured(false); }
      } catch { /* rail simply stays hidden */ }
    })();
    return () => { cancelled = true; };
  }, [article, slug]);

  // Reading progress — thin purple bar pinned to the top, width follows scroll.
  const progress = useSharedValue(0);
  const onScroll = useAnimatedScrollHandler((e) => {
    const max = e.contentSize.height - e.layoutMeasurement.height;
    progress.value = max > 0 ? Math.min(1, Math.max(0, e.contentOffset.y / max)) : 0;
  });
  const progressStyle = useAnimatedStyle(() => ({ width: progress.value * width }));

  const rawBody = (article?.content || article?.body || '').toString();
  // Pull embedded ![](url) images into a carousel; render the rest as prose.
  const { urls: bodyImages, cleaned: body } = useMemo(
    () => extractBodyImages(rawBody),
    [rawBody],
  );
  // Forward-compatible: if the model ever gains an `images`/`gallery` array
  // (see followUps), surface those in the carousel too — de-duped, hero excluded.
  // #44: the article's real cover, else a stable topic-themed featured image so
  // the hero is a magazine cover — never the flat purple gradient placeholder.
  const cover = article
    ? learnCover(article.title, article.slug, article.image || article.imageUrl, 1000)
    : (article?.image || article?.imageUrl);
  const galleryField: string[] = Array.isArray(article?.images)
    ? article.images.filter((u: any) => typeof u === 'string')
    : Array.isArray(article?.gallery)
      ? article.gallery.filter((u: any) => typeof u === 'string')
      : [];
  const carouselImages = useMemo(() => {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const u of [...galleryField, ...bodyImages]) {
      if (u && u !== cover && !seen.has(u)) { seen.add(u); out.push(u); }
    }
    return out;
  }, [galleryField, bodyImages, cover]);
  const blocks = parseBody(body);
  const readMins = Math.max(1, Math.round(body.split(/\s+/).filter(Boolean).length / 200));
  const views = typeof article?.views === 'number' ? article.views : null;
  const author = article?.author || article?.User || null;
  const authorName = author?.displayName || author?.username || null;
  const published = article?.createdAt ? new Date(article.createdAt) : null;
  const dateLabel = published && !isNaN(published.getTime())
    ? published.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
    : null;
  const heroHeight = 340 + insets.top;
  const hasCover = !!cover;
  // Carousel card width: a peek of the next card on phones, capped on wide screens.
  const contentW = layout !== 'phone' ? 680 : width;
  const carouselW = Math.round(Math.min(contentW - spacing.xl * 2, contentW * 0.82));

  const renderBlock = (block: Block, i: number) => {
    switch (block.type) {
      case 'heading':
        return (
          <Text key={i} style={block.level === 2 ? styles.h2 : styles.h3}>
            {renderInline(block.text, `h${i}`)}
          </Text>
        );
      case 'bullets':
        return (
          <View key={i} style={styles.listBlock}>
            {block.items.map((item, j) => (
              <View key={j} style={styles.listRow}>
                <View style={styles.bulletDot} />
                <Text style={styles.listText}>{renderInline(item, `b${i}-${j}`)}</Text>
              </View>
            ))}
          </View>
        );
      case 'ordered':
        return (
          <View key={i} style={styles.listBlock}>
            {block.items.map((item, j) => (
              <View key={j} style={styles.listRow}>
                <Text style={styles.orderedNum}>{j + 1}.</Text>
                <Text style={styles.listText}>{renderInline(item, `o${i}-${j}`)}</Text>
              </View>
            ))}
          </View>
        );
      case 'quote':
        return (
          <View key={i} style={styles.quoteBlock}>
            <Text style={styles.quoteText}>{renderInline(block.text, `q${i}`)}</Text>
          </View>
        );
      case 'code':
        return (
          <View key={i} style={styles.codeBlock}>
            <Text style={styles.codeText}>{block.text}</Text>
          </View>
        );
      default:
        return (
          <Text key={i} style={styles.paragraph}>
            {renderInline(block.text, `p${i}`)}
          </Text>
        );
    }
  };

  if (loading) {
    return (
      <View style={styles.screen}>
        <ParticleBackground style={StyleSheet.absoluteFill} theme="light" count={5} />
        <View style={StyleSheet.absoluteFill}>
          <LoadingState message="Loading…" onRetry={fetchArticle} />
        </View>
      </View>
    );
  }

  if (error || !article) {
    return (
      <View style={styles.screen}>
        <ParticleBackground style={StyleSheet.absoluteFill} theme="light" count={5} />
        <View style={[StyleSheet.absoluteFill, styles.center]}>
        <Asset3D name="error" size={120} style={styles.errorArt} />
        <Text style={styles.errorText}>This lesson isn't available.</Text>
        <TouchableOpacity
          style={styles.errorBackBtn}
          onPress={() => navigation.goBack()}
          activeOpacity={0.8}
          accessibilityRole="button"
          accessibilityLabel="Go back to Learn"
        >
          <Ionicons name="arrow-back" size={16} color={colors.white} />
          <Text style={styles.errorBackText}>Back to Learn</Text>
        </TouchableOpacity>
        </View>
      </View>
    );
  }

  return (
    <View style={styles.screen}>
      {/* Full-bleed aurora under the immersive hero + content sheet (skill §12).
          The hero image sits opaque on top of it; below the hero the translucent
          sheet lets the aurora bleed through the reading column. */}
      <ParticleBackground style={StyleSheet.absoluteFill} theme="light" count={5} />

      <Animated.ScrollView
        onScroll={onScroll}
        scrollEventThrottle={16}
        showsVerticalScrollIndicator={false}
        contentContainerStyle={styles.scrollContent}
      >
        {/* Full-bleed hero: the article's REAL cover edge-to-edge (or a brand
            gradient fallback when the payload has none). Category/level pills,
            title and meta sit OVER the image on a dark gradient scrim so the
            header reads as a magazine cover, not a placeholder. */}
        <View style={[styles.hero, { height: heroHeight }]}>
          {hasCover ? (
            <CachedImage source={{ uri: cover }} style={StyleSheet.absoluteFill} resizeMode="cover" />
          ) : (
            <LinearGradient
              colors={[...CARD_GRADIENTS[0]]}
              start={{ x: 0, y: 0 }}
              end={{ x: 1, y: 1 }}
              style={[StyleSheet.absoluteFill, styles.heroFallback]}
            >
              <Ionicons name="school" size={64} color="rgba(255,255,255,0.28)" />
            </LinearGradient>
          )}
          {/* Dark scrim: readable text at the base, fades to the page below. */}
          <LinearGradient
            colors={['rgba(14,11,26,0.15)', 'rgba(14,11,26,0.45)', 'rgba(14,11,26,0.94)']}
            locations={[0, 0.5, 1]}
            style={StyleSheet.absoluteFill}
          />

          {/* Title block overlaid on the cover. */}
          <View style={[styles.heroContent, containerStyle, { paddingTop: insets.top + 56 }]}>
            <View style={styles.pillRow}>
              {!!article.category && (
                <View style={styles.categoryPill}>
                  <Text style={styles.categoryText}>{String(article.category).replace(/_/g, ' ')}</Text>
                </View>
              )}
              {!!article.difficulty && (
                <View style={styles.levelPill}>
                  <Text style={styles.levelText}>{levelLabel(article.difficulty)}</Text>
                </View>
              )}
            </View>

            <Text style={styles.title}>{article.title}</Text>

            <View style={styles.metaRow}>
              {author?.avatar ? (
                <CachedImage source={{ uri: author.avatar }} style={styles.avatar} />
              ) : authorName ? (
                <View style={[styles.avatar, styles.avatarFallback]}>
                  <Text style={styles.avatarInitial}>{authorName.charAt(0).toUpperCase()}</Text>
                </View>
              ) : null}
              <View style={styles.metaTextCol}>
                {!!authorName && <Text style={styles.metaAuthor}>{authorName}</Text>}
                <View style={styles.metaLine}>
                  {!!dateLabel && <Text style={styles.metaText}>{dateLabel}</Text>}
                  {!!dateLabel && <Text style={styles.metaDot}>·</Text>}
                  <Text style={[styles.metaText, tabularNums]}>{readMins} min read</Text>
                  {views !== null && (
                    <>
                      <Text style={styles.metaDot}>·</Text>
                      <Text style={[styles.metaText, tabularNums]}>{views.toLocaleString()} views</Text>
                    </>
                  )}
                </View>
              </View>
            </View>
          </View>
        </View>

        {/* Content sheet overlapping the hero — dark reading surface. */}
        <View style={[styles.sheet, containerStyle]}>
          {!!(article.description || article.excerpt) && (
            <Text style={styles.lede}>{article.description || article.excerpt}</Text>
          )}

          <View style={styles.divider} />

          {/* Inline image carousel — embedded body images (![](url)) or a
              future images[] field, shown as a snapping horizontal gallery. */}
          {carouselImages.length > 0 && (
            <View style={styles.carouselWrap}>
              <ScrollView
                horizontal
                showsHorizontalScrollIndicator={false}
                snapToInterval={carouselW + spacing.md}
                snapToAlignment="start"
                decelerationRate="fast"
                contentContainerStyle={styles.carouselContent}
              >
                {carouselImages.map((uri, i) => (
                  <View key={`${uri}-${i}`} style={[styles.carouselCard, { width: carouselW }]}>
                    <CachedImage source={{ uri }} style={StyleSheet.absoluteFill} resizeMode="cover" />
                  </View>
                ))}
              </ScrollView>
              {carouselImages.length > 1 && (
                <Text style={[styles.carouselHint, tabularNums]}>
                  {carouselImages.length} images · swipe to explore
                </Text>
              )}
            </View>
          )}

          {blocks.length > 0
            ? blocks.map(renderBlock)
            : <Text style={styles.paragraph}>{body}</Text>}

          {/* Keep-reading rail — admin-curated Must-see (or same-level recents).
              MustSeeRail renders nothing when the list is empty. */}
          {more.length > 0 && <View style={styles.railDivider} />}
          <MustSeeRail
            articles={more}
            onPressArticle={(s) => navigation.push('LearnArticle', { slug: s })}
            kicker="KEEP LEARNING"
            title={moreIsFeatured ? 'Must-see articles' : 'More lessons like this'}
            showBadge={moreIsFeatured}
            bleed={spacing.xl}
            style={styles.rail}
          />

          {/* Gamified knowledge check — pass to unlock the next lesson + earn XP. */}
          <QuizSheet
            slug={article.slug}
            title={article.title}
            onPassed={() => { /* progress store already updated; Learn screen re-derives on focus */ }}
          />

          {/* End-of-article CTA — no empty void: send the reader onward to more
              lessons and to the communities. Glass surfaces, tokens throughout. */}
          <View style={styles.ctaSection}>
            <Text style={styles.ctaKicker}>WHAT'S NEXT</Text>
            <Text style={styles.ctaTitle}>Keep the momentum going</Text>
            <View style={styles.ctaRow}>
              <GlassSurface
                variant="card"
                radius={radius.xl}
                onPress={() => navigation.navigate('LearnIndex')}
                style={styles.ctaCard}
                contentStyle={styles.ctaCardInner}
                accessibilityRole="button"
                accessibilityLabel="Browse more lessons"
              >
                <View style={styles.ctaIcon}>
                  <Ionicons name="library-outline" size={22} color={colors.primary} />
                </View>
                <Text style={styles.ctaCardTitle}>More lessons</Text>
                <Text style={styles.ctaCardSub}>Browse the full Learn Center</Text>
              </GlassSurface>
              <GlassSurface
                variant="card"
                radius={radius.xl}
                onPress={() => navigation.navigate('Communities')}
                style={styles.ctaCard}
                contentStyle={styles.ctaCardInner}
                accessibilityRole="button"
                accessibilityLabel="Discover communities"
              >
                <View style={styles.ctaIcon}>
                  <Ionicons name="people-outline" size={22} color={colors.primary} />
                </View>
                <Text style={styles.ctaCardTitle}>Discover communities</Text>
                <Text style={styles.ctaCardSub}>Find your people on CRYB</Text>
              </GlassSurface>
            </View>

            {/* #59: real communities to join, not just a teaser. */}
            {nextCommunities.length > 0 && (
              <ScrollView
                horizontal
                showsHorizontalScrollIndicator={false}
                contentContainerStyle={styles.nextCommRail}
              >
                {nextCommunities.map((c: any, i: number) => {
                  const name = c.name || c.displayName || '';
                  const title = c.displayName || c.name || 'Community';
                  const img = communityIcon(c.name, c.displayName, c.icon || c.avatar || c.imageUrl);
                  const members = c.memberCount ?? c.members ?? null;
                  return (
                    <TouchableOpacity
                      key={c.id || name || i}
                      style={styles.nextCommCard}
                      activeOpacity={0.85}
                      onPress={() => name && navigation.navigate('Community', { name })}
                      accessibilityRole="button"
                      accessibilityLabel={`Open ${title}`}
                    >
                      {img ? (
                        <CachedImage source={{ uri: img }} style={styles.nextCommAvatar} />
                      ) : (
                        <View style={[styles.nextCommAvatar, { backgroundColor: colors.primary + '18', alignItems: 'center', justifyContent: 'center' }]}>
                          <Text style={{ color: colors.primary, fontWeight: '800', fontSize: 20 }}>{title.charAt(0).toUpperCase()}</Text>
                        </View>
                      )}
                      <Text style={styles.nextCommName} numberOfLines={1}>{title}</Text>
                      {members != null && (
                        <Text style={styles.nextCommMeta} numberOfLines={1}>{Number(members).toLocaleString()} members</Text>
                      )}
                    </TouchableOpacity>
                  );
                })}
              </ScrollView>
            )}
          </View>
        </View>
      </Animated.ScrollView>

      {/* Floating glass back button over the hero. */}
      <TouchableOpacity
        style={[styles.backFab, { top: insets.top + spacing.sm }]}
        onPress={() => navigation.goBack()}
        activeOpacity={0.8}
        accessibilityRole="button"
        accessibilityLabel="Go back"
        hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
      >
        <Ionicons name="arrow-back" size={20} color={colors.ink} />
      </TouchableOpacity>

      {/* Reading progress — thin purple bar pinned to the top. */}
      <View style={[styles.progressTrack, { top: insets.top }]} pointerEvents="none">
        <Animated.View style={[styles.progressFill, progressStyle]} />
      </View>
    </View>
  );
}

const inlineStyles = StyleSheet.create({
  bold: { ...fonts.bold, color: colors.ink },
  italic: { fontStyle: 'italic' },
  code: {
    fontFamily: 'Menlo',
    fontSize: 15,
    color: colors.primary,
    backgroundColor: colors.primary + '12',
  },
  link: {
    color: colors.primary,
    textDecorationLine: 'underline',
    ...fonts.medium,
  },
});

const styles = StyleSheet.create({
  // Transparent so the ParticleBackground aurora reads through the status bar
  // and (via the translucent sheet) the reading column.
  screen: { flex: 1, backgroundColor: 'transparent' },
  center: { justifyContent: 'center', alignItems: 'center', padding: spacing.xxxl },
  scrollContent: { paddingBottom: 0 },

  // Error state
  errorArt: { marginBottom: spacing.md },
  errorText: { ...fonts.medium, fontSize: 15, color: colors.muted },
  errorBackBtn: {
    flexDirection: 'row', alignItems: 'center', gap: spacing.sm,
    marginTop: spacing.lg, backgroundColor: colors.primary, borderRadius: radius.full,
    paddingHorizontal: spacing.xl, paddingVertical: spacing.sm + 2,
  },
  errorBackText: { ...fonts.semibold, fontSize: 14, color: colors.white },

  // Hero
  hero: { width: '100%', backgroundColor: colors.card },
  heroFallback: { alignItems: 'center', justifyContent: 'center' },
  heroContent: {
    position: 'absolute',
    left: 0, right: 0, bottom: 0,
    paddingHorizontal: spacing.xl,
    // Clear the sheet: the content sheet rises -28 over the hero, so the author
    // meta row must sit MORE than 28 above the hero's bottom or the white sheet
    // edge slices through it (the reported overlap). xxl + lg ≈ 48 → ~20 clearance.
    paddingBottom: spacing.xxl + spacing.lg,
  },

  // Floating chrome
  backFab: {
    position: 'absolute',
    left: spacing.lg,
    width: 40,
    height: 40,
    borderRadius: 20,
    // Light glass chip so the back arrow stays visible over any hero (dark or
    // busy). A soft shadow lifts it off the image.
    backgroundColor: 'rgba(255,255,255,0.92)',
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: 'rgba(0,0,0,0.06)',
    alignItems: 'center',
    justifyContent: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.18,
    shadowRadius: 6,
    zIndex: 30,
  },
  progressTrack: {
    position: 'absolute',
    left: 0,
    right: 0,
    height: 3,
    backgroundColor: 'rgba(108,92,231,0.15)',
    zIndex: 20,
  },
  progressFill: { height: 3, backgroundColor: colors.primary },

  // Content sheet — a dark reading surface that rises over the hero (skill §12).
  // A bright hairline top edge reads as light catching the material as it
  // arrives; the aurora bleeds subtly through the reading column.
  sheet: {
    marginTop: -28,
    borderTopLeftRadius: 28,
    borderTopRightRadius: 28,
    // Light reading surface (was a dark-mode leftover rgba(23,20,39) → "dark
    // background inside articles"). White sheet + dark ink text = the light-glass brand.
    backgroundColor: '#FFFFFF',
    borderTopWidth: 1,
    borderColor: colors.cardBorder,
    paddingHorizontal: spacing.xl,
    paddingTop: spacing.xxl,
    // White extends past the last content so no aurora/gradient shows below the
    // "what's next" section (#80). The scroll's own bottom pad is removed.
    paddingBottom: spacing.xxxl * 2,
    shadowColor: colors.primary,
    shadowOffset: { width: 0, height: -6 },
    shadowOpacity: 0.12,
    shadowRadius: 24,
    elevation: 12,
  },
  // Pills/title/meta live OVER the cover image (white on a dark scrim).
  pillRow: { flexDirection: 'row', gap: spacing.sm, marginBottom: spacing.md },
  categoryPill: {
    backgroundColor: 'rgba(255,255,255,0.18)', borderRadius: radius.full,
    borderWidth: StyleSheet.hairlineWidth, borderColor: 'rgba(255,255,255,0.28)',
    paddingHorizontal: spacing.md, paddingVertical: 4,
  },
  categoryText: {
    ...fonts.bold, fontSize: 11, color: '#FFFFFF',
    textTransform: 'uppercase', letterSpacing: 0.5,
  },
  levelPill: {
    backgroundColor: colors.primary + 'CC', borderRadius: radius.full,
    paddingHorizontal: spacing.md, paddingVertical: 4,
  },
  levelText: {
    ...fonts.bold, fontSize: 11, color: '#FFFFFF',
    textTransform: 'uppercase', letterSpacing: 0.5,
  },
  title: {
    ...fonts.extrabold, fontSize: 30, lineHeight: 38,
    letterSpacing: -0.5, color: '#FFFFFF',
    textShadowColor: 'rgba(0,0,0,0.45)',
    textShadowOffset: { width: 0, height: 1 },
    textShadowRadius: 12,
  },
  metaRow: {
    flexDirection: 'row', alignItems: 'center', gap: spacing.md,
    marginTop: spacing.lg,
  },
  avatar: {
    width: 36, height: 36, borderRadius: 18, backgroundColor: 'rgba(255,255,255,0.15)',
    borderWidth: StyleSheet.hairlineWidth, borderColor: 'rgba(255,255,255,0.35)',
  },
  avatarFallback: { alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(255,255,255,0.22)' },
  avatarInitial: { ...fonts.bold, fontSize: 15, color: '#FFFFFF' },
  metaTextCol: { flex: 1, gap: 1 },
  metaAuthor: { ...fonts.semibold, fontSize: 14, color: '#FFFFFF' },
  metaLine: { flexDirection: 'row', alignItems: 'center', gap: spacing.xs },
  metaText: { ...fonts.medium, fontSize: 13, color: 'rgba(255,255,255,0.82)' },
  metaDot: { ...fonts.medium, fontSize: 13, color: 'rgba(255,255,255,0.6)' },
  lede: {
    ...fonts.medium, fontSize: 18, color: colors.ink,
    marginTop: 0, lineHeight: 28,
  },
  divider: {
    height: 3, width: 44, borderRadius: 2, backgroundColor: colors.primary + '55',
    marginTop: spacing.xl,
  },

  // Body typography
  h2: {
    ...fonts.extrabold, fontSize: 22, color: colors.ink,
    lineHeight: 28, marginTop: spacing.xxl, letterSpacing: -0.3,
  },
  h3: {
    ...fonts.bold, fontSize: 18, color: colors.ink,
    lineHeight: 24, marginTop: spacing.xl,
  },
  paragraph: {
    ...fonts.regular, fontSize: 17, color: colors.ink2,
    lineHeight: 28, marginTop: spacing.lg,
  },
  listBlock: { marginTop: spacing.lg, gap: spacing.sm },
  listRow: { flexDirection: 'row', alignItems: 'flex-start', gap: spacing.md },
  bulletDot: {
    width: 6, height: 6, borderRadius: 3, backgroundColor: colors.primary,
    marginTop: 10,
  },
  orderedNum: { ...fonts.bold, fontSize: 16, color: colors.primary, lineHeight: 26, minWidth: 20 },
  listText: {
    flex: 1, ...fonts.regular, fontSize: 17, color: colors.ink2, lineHeight: 26,
  },
  quoteBlock: {
    marginTop: spacing.lg,
    borderLeftWidth: 3,
    borderLeftColor: colors.primary,
    backgroundColor: colors.primary + '0D',
    borderRadius: radius.md,
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.lg,
  },
  quoteText: {
    ...fonts.medium, fontSize: 17, color: colors.ink2,
    lineHeight: 27, fontStyle: 'italic',
  },
  codeBlock: {
    marginTop: spacing.lg,
    backgroundColor: '#0B0916',
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: colors.cardBorder,
    borderRadius: radius.lg,
    padding: spacing.lg,
  },
  codeText: {
    fontFamily: 'Menlo',
    fontSize: 13,
    lineHeight: 20,
    color: '#E8E1F2',
  },

  // Inline image carousel
  carouselWrap: { marginTop: spacing.xl },
  carouselContent: { gap: spacing.md },
  carouselCard: {
    height: 220,
    borderRadius: radius.xl,
    overflow: 'hidden',
    backgroundColor: colors.card,
  },
  carouselHint: {
    ...fonts.medium, fontSize: 12, color: colors.muted,
    marginTop: spacing.sm, letterSpacing: 0.2,
  },

  // Keep-reading rail
  railDivider: {
    height: 1, backgroundColor: colors.cardBorder,
    marginTop: spacing.xxxl,
  },
  rail: { marginTop: spacing.xxl },

  // End-of-article CTA
  ctaSection: { marginTop: spacing.xxxl },
  ctaKicker: {
    ...fonts.bold, fontSize: 11, color: colors.primary,
    letterSpacing: 1.2, marginBottom: 2,
  },
  ctaTitle: {
    ...fonts.extrabold, fontSize: 20, color: colors.ink,
    marginBottom: spacing.md,
  },
  ctaRow: { flexDirection: 'row', gap: spacing.md },
  ctaCard: { flex: 1, borderRadius: radius.xl },
  ctaCardInner: {
    padding: spacing.lg,
    minHeight: 132,
    justifyContent: 'flex-start',
    gap: spacing.xs,
  },
  ctaIcon: {
    width: 44, height: 44, borderRadius: radius.md,
    backgroundColor: colors.primary + '14',
    alignItems: 'center', justifyContent: 'center',
    marginBottom: spacing.sm,
  },
  ctaCardTitle: { ...fonts.bold, fontSize: 15, color: colors.ink },
  ctaCardSub: { ...fonts.regular, fontSize: 12, color: colors.muted, lineHeight: 17 },
  nextCommRail: { gap: spacing.md, paddingTop: spacing.lg, paddingRight: spacing.xl },
  nextCommCard: { width: 108, alignItems: 'center' },
  nextCommAvatar: { width: 64, height: 64, borderRadius: 32, marginBottom: spacing.sm, backgroundColor: colors.primary + '10' },
  nextCommName: { ...fonts.semibold, fontSize: 13, color: colors.ink, textAlign: 'center' },
  nextCommMeta: { ...fonts.regular, fontSize: 11, color: colors.muted, textAlign: 'center', marginTop: 2 },
});
